// App registry — serializable metadata plus lazy component loaders.

const componentLoaders = {
  welcome: () => import('../apps/Welcome.svelte'),
  'about-me': () => import('../apps/AboutMe.svelte'),
  projects: () => import('../apps/Finder.svelte'),
  resume: () => import('../apps/Resume.svelte'),
  chat: () => import('../apps/Chat.svelte'),
  game: () => import('../apps/Game.svelte'),
  contact: () => import('../apps/Contact.svelte'),
  about: () => import('../apps/About.svelte'),
  'control-panel': () => import('../apps/ControlPanel.svelte'),
  'content-editor': () => import('../apps/ContentEditor.svelte'),
  textedit: () => import('../apps/TextEdit.svelte'),
  preview: () => import('../apps/Preview.svelte'),
  trash: () => import('../apps/Trash.svelte'),
  console: () => import('../apps/Console.svelte'),
  'disk-utility': () => import('../apps/DiskUtility.svelte'),
  weather: () => import('../apps/Weather.svelte'),
  calculator: () => import('../apps/Calculator.svelte'),
  terminal: () => import('../apps/Terminal.svelte'),
  notes: () => import('../apps/Notes.svelte'),
  safari: () => import('../apps/Safari.svelte'),
  'activity-monitor': () => import('../apps/ActivityMonitor.svelte'),
  'software-update': () => import('../apps/SoftwareUpdate.svelte'),
  stickies: () => import('../apps/Stickies.svelte'),
  contacts: () => import('../apps/Contacts.svelte'),
  calendar: () => import('../apps/Calendar.svelte'),
  gallery: () => import('../apps/Gallery.svelte'),
  quicktime: () => import('../apps/QuickTimePlayer.svelte'),
  aquapaint: () => import('../apps/AquaPaint.svelte'),
  music: () => import('../apps/Music.svelte'),
  freeform: () => import('../apps/Freeform.svelte'),
  photos: () => import('../apps/Photos.svelte'),
  photobooth: () => import('../apps/PhotoBooth.svelte'),
  journal: () => import('../apps/Journal.svelte'),
  books: () => import('../apps/Books.svelte'),
  'screen-capture': () => import('../apps/ScreenCapture.svelte'),
  'font-book': () => import('../apps/FontBook.svelte'),
  dictionary: () => import('../apps/Dictionary.svelte'),
  pages: () => import('../apps/Pages.svelte'),
  numbers: () => import('../apps/Numbers.svelte'),
  keynote: () => import('../apps/Keynote.svelte'),
  'dvd-player': () => import('../apps/DVDPlayer.svelte'),
  mail: () => import('../apps/Mail.svelte'),
  facetime: () => import('../apps/FaceTime.svelte'),
  'code-editor': () => import('../apps/CodeEditor.svelte'),
  'neovim-config': () => import('../apps/NeovimConfig.svelte'),
  'git-viewer': () => import('../apps/GitViewer.svelte'),
  'api-client': () => import('../apps/ApiClient.svelte'),
  'web-inspector': () => import('../apps/WebInspector.svelte'),
  'game-center': () => import('../apps/GameCenter.svelte'),
  chess: () => import('../apps/Chess.svelte'),
  solitaire: () => import('../apps/Solitaire.svelte'),
  steam: () => import('../apps/Steam.svelte'),
  dashboard: () => import('../apps/Dashboard.svelte'),
  visualizer: () => import('../apps/Visualizer.svelte'),
  notchnest: () => import('../apps/NotchNest.svelte'),
  'infra-ops': () => import('../apps/InfraOps/InfraOps.svelte'),
};

function app(id, label, icon, options = {}) {
  return {
    id,
    label,
    icon,
    loader: componentLoaders[id],
    w: 440,
    category: 'utilities',
    singleton: true,
    contentInset: 'standard',
    dock: false,
    desktop: true,
    ...options,
  };
}

export const APPS = [
  app('welcome', 'Welcome', 'welcome', { w: 470, category: 'portfolio' }),
  app('about-me', 'About Me', 'document', {
    w: 430,
    h: 540,
    category: 'portfolio',
    contentInset: 'none',
  }),
  app('projects', 'Finder', 'finder', {
    w: 860,
    h: 520,
    category: 'system',
    singleton: false,
    contentInset: 'none',
    dock: true,
  }),
  app('resume', 'Resume', 'resume', { w: 560, category: 'portfolio' }),
  app('chat', 'Messages', 'chat', { w: 390, category: 'communication', dock: true }),
  app('game', 'Graphite Tiles', 'game', { w: 430, category: 'entertainment' }),
  app('contact', 'Contact', 'contact-card', { w: 430, category: 'portfolio' }),
  app('about', 'About This Mac', 'computer', { w: 450, category: 'system' }),
  app('control-panel', 'System Preferences', 'gear', {
    w: 700,
    h: 540,
    category: 'system',
    contentInset: 'none',
    dock: true,
  }),
  app('content-editor', 'Content Editor', 'content-editor', {
    w: 720,
    category: 'admin',
    adminOnly: true,
  }),
  app('infra-ops', 'Infrastructure Ops', 'infra-ops', {
    w: 940,
    h: 620,
    category: 'admin',
    adminOnly: true,
    contentInset: 'none',
    dock: false,
  }),

  app('textedit', 'TextEdit', 'textedit', {
    w: 620,
    h: 500,
    category: 'documents',
    singleton: false,
    contentInset: 'none',
    dock: true,
    fileTypes: ['.txt', '.md', '.rtf', '.log'],
  }),
  app('preview', 'Preview', 'preview', {
    w: 820,
    h: 620,
    category: 'documents',
    singleton: false,
    contentInset: 'none',
    dock: true,
    fileTypes: ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.bmp'],
  }),
  app('trash', 'Trash', 'trash', {
    w: 640,
    h: 430,
    category: 'system',
    contentInset: 'none',
    dock: false,
    desktop: false,
  }),
  app('calculator', 'Calculator', 'calculator', { w: 246, h: 380, contentInset: 'none' }),
  app('terminal', 'Terminal', 'terminal', {
    w: 700,
    h: 440,
    category: 'dev',
    singleton: false,
    contentInset: 'none',
  }),
  app('notes', 'Notes', 'notes', {
    w: 780,
    h: 540,
    category: 'documents',
    contentInset: 'none',
    dock: true,
  }),

  app('safari', 'Safari', 'safari', {
    w: 1000,
    h: 680,
    category: 'web',
    singleton: false,
    contentInset: 'none',
    dock: true,
  }),
  app('activity-monitor', 'Activity Monitor', 'activity-monitor', {
    w: 600,
    h: 400,
    contentInset: 'none',
  }),
  app('console', 'Console', 'console', {
    w: 720,
    h: 440,
    category: 'utilities',
    contentInset: 'none',
  }),
  app('disk-utility', 'Disk Utility', 'hard-drive', {
    w: 660,
    h: 440,
    category: 'utilities',
    contentInset: 'none',
  }),
  app('weather', 'Weather', 'cloud', {
    w: 540,
    h: 360,
    category: 'utilities',
    contentInset: 'none',
  }),
  app('software-update', 'Software Update', 'software-update', {
    w: 480,
    h: 380,
    category: 'system',
    contentInset: 'none',
  }),
  app('stickies', 'Stickies', 'stickies', {
    w: 240,
    h: 200,
    singleton: false,
    contentInset: 'none',
  }),
  app('contacts', 'Contacts', 'address-book', {
    w: 620,
    h: 460,
    category: 'communication',
    contentInset: 'none',
  }),
  app('calendar', 'Calendar', 'calendar', {
    w: 820,
    h: 580,
    category: 'productivity',
    contentInset: 'none',
    dock: true,
  }),
  app('gallery', 'Gallery', 'gallery', { w: 820, h: 560, category: 'media', contentInset: 'none' }),
  app('quicktime', 'QuickTime Player', 'quicktime', {
    w: 640,
    h: 420,
    category: 'media',
    singleton: false,
    contentInset: 'none',
    fileTypes: ['.mp4', '.mov', '.webm', '.mp3', '.ogg', '.wav'],
  }),
  app('aquapaint', 'AquaPaint', 'aquapaint', {
    w: 700,
    h: 500,
    category: 'media',
    contentInset: 'none',
  }),
  app('music', 'Music', 'music', {
    w: 800,
    h: 500,
    category: 'media',
    contentInset: 'none',
    dock: true,
  }),
  app('freeform', 'Freeform', 'freeform', {
    w: 1000,
    h: 700,
    category: 'productivity',
    contentInset: 'none',
  }),
  app('photos', 'Photos', 'photos', {
    w: 980,
    h: 640,
    category: 'media',
    contentInset: 'none',
    dock: true,
  }),
  app('photobooth', 'Photo Booth', 'photo-booth', {
    w: 760,
    h: 600,
    category: 'media',
    contentInset: 'none',
  }),
  app('journal', 'Journal', 'journal', {
    w: 820,
    h: 600,
    category: 'documents',
    contentInset: 'none',
  }),
  app('books', 'Books', 'books', {
    w: 860,
    h: 620,
    category: 'documents',
    contentInset: 'none',
    fileTypes: ['.epub'],
  }),
  app('screen-capture', 'Screen Capture', 'screen-capture', {
    w: 720,
    h: 520,
    category: 'utilities',
    contentInset: 'none',
  }),
  app('font-book', 'Font Book', 'font-book', {
    w: 760,
    h: 540,
    category: 'utilities',
    contentInset: 'none',
  }),
  app('dictionary', 'Dictionary', 'dictionary', {
    w: 760,
    h: 520,
    category: 'reference',
    contentInset: 'none',
  }),

  // === Phase 4: Office Suite ===
  app('pages', 'Pages', 'pages', {
    w: 820,
    h: 620,
    category: 'documents',
    singleton: false,
    contentInset: 'none',
    fileTypes: ['.pages'],
  }),
  app('numbers', 'Numbers', 'spreadsheet', {
    w: 900,
    h: 600,
    category: 'documents',
    singleton: false,
    contentInset: 'none',
    fileTypes: ['.numbers'],
  }),
  app('keynote', 'Keynote', 'keynote', {
    w: 960,
    h: 680,
    category: 'media',
    singleton: false,
    contentInset: 'none',
    fileTypes: ['.key'],
  }),
  app('dvd-player', 'DVD Player', 'dvd-player', {
    w: 640,
    h: 400,
    category: 'media',
    contentInset: 'none',
  }),

  // === Phase 5: Communication ===
  app('mail', 'Mail', 'mail', {
    w: 820,
    h: 580,
    category: 'communication',
    contentInset: 'none',
    dock: true,
  }),
  app('facetime', 'FaceTime', 'facetime', {
    w: 640,
    h: 520,
    category: 'communication',
    contentInset: 'none',
  }),

  // === Phase 6: Developer Workstation ===
  app('code-editor', 'Code Editor', 'code-editor', {
    w: 900,
    h: 640,
    category: 'dev',
    contentInset: 'none',
    singleton: false,
  }),
  app('neovim-config', 'Neovim Config', 'neovim', {
    w: 860,
    h: 580,
    category: 'dev',
    contentInset: 'none',
  }),
  app('git-viewer', 'Git Viewer', 'git', { w: 800, h: 560, category: 'dev', contentInset: 'none' }),
  app('api-client', 'API Client', 'api', { w: 880, h: 580, category: 'dev', contentInset: 'none' }),
  app('web-inspector', 'Web Inspector', 'web-inspector', {
    w: 860,
    h: 500,
    category: 'dev',
    contentInset: 'none',
  }),

  // === Phase 7: Entertainment Layer ===
  app('game-center', 'Game Center', 'game-center', {
    w: 700,
    h: 500,
    category: 'entertainment',
    contentInset: 'none',
  }),
  app('chess', 'Chess', 'chess-piece', {
    w: 560,
    h: 580,
    category: 'entertainment',
    contentInset: 'none',
  }),
  app('solitaire', 'Solitaire', 'cards', {
    w: 680,
    h: 520,
    category: 'entertainment',
    contentInset: 'none',
  }),
  app('steam', 'Steam', 'steam', {
    w: 820,
    h: 580,
    category: 'entertainment',
    contentInset: 'none',
  }),
  app('dashboard', 'Dashboard', 'dashboard', {
    w: 820,
    h: 600,
    category: 'entertainment',
    contentInset: 'none',
  }),
  app('visualizer', 'Visualizer', 'visualizer', {
    w: 820,
    h: 600,
    category: 'entertainment',
    contentInset: 'none',
  }),
  app('notchnest', 'NotchNest', 'notch', {
    w: 820,
    h: 500,
    category: 'entertainment',
    contentInset: 'none',
  }),
];

const componentCache = new Map();

export const appById = (id) => APPS.find((entry) => entry.id === id);

export async function loadAppComponent(appOrId) {
  const appEntry = typeof appOrId === 'string' ? appById(appOrId) : appOrId;
  if (!appEntry?.loader) throw new Error(`Unknown app: ${String(appOrId)}`);
  if (!componentCache.has(appEntry.id)) {
    componentCache.set(
      appEntry.id,
      appEntry.loader().then((module) => module.default)
    );
  }
  return componentCache.get(appEntry.id);
}

export function appsForExtension(extension) {
  const normalized = extension.toLowerCase();
  return APPS.filter((entry) => entry.fileTypes?.includes(normalized));
}
